import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import FolderList from "../../components/Common/FolderList";
import { FolderItemContainer } from "../../components/Common/FolderItem";
import ViewTitle from "../../components/Common/ViewTitle";
import { useIntl } from "react-intl";
import { getServiceTypeLanguageIdByName } from "../../modules/ticket/ticketConstants";
import { getStateByID } from "../../functions/getStateByID";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import { hasEntries } from "../../functions/array";
import { history } from "../../store";
import DocumentLayout from "../../components/Layouts/DocumentLayout";
import { Paper, Box } from "@mui/material";
import TicketFilterMenu from "./TicketFilterMenu";
import {
  selectTicketItems,
  selectTicketListFilter,
  selectTicketsStatus,
} from "../../modules/ticket/ticketList/ticketListSlice";
import CircularProgress from "@mui/material/CircularProgress";
import { setTicketRead } from "../../modules/ticket/ticketsRead/ticketsReadActions";
import { REQUEST_STATUS } from "../../functions/constants";
import { fetchTicketsByEstateIdThunk } from "../../modules/ticket/ticketList/ticketListSlice";
import { currentEstateSelectors } from "../../modules/estate/currentEstate/currentEstateSlice";

/**
 * Dashboard component. Container for tickets related to a Liegenschaft
 */
function TicketListContainer(props) {
  const { title, ...rest } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const formatDocumentWithIntl = formatDocument(intl.formatMessage);

  const tickets = useSelector(selectTicketItems);
  const ticketListFilter = useSelector(selectTicketListFilter);
  const status = useSelector(selectTicketsStatus);
  const estate = useSelector(currentEstateSelectors.selectCurrentEstate);
  const readTicketsIds = useSelector((state) => state.tickets.readTicketsIds);
  const hasNoTickets = (ticketsToCheck) => !hasEntries(ticketsToCheck);

  const handleOpenTicket = (id) => {
    dispatch(setTicketRead(id));
    history.push(`/tickets/${id}`);
  };

  useEffect(() => {
    dispatch(fetchTicketsByEstateIdThunk(estate.id));
  }, [dispatch, estate.id, ticketListFilter]);

  const handleItemClick = (id) => dispatch(setTicketRead(id));
  return (
    <DocumentLayout>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pl: 2,
          pr: 1,
          mb: 1,
        }}
      >
        <ViewTitle title={title || "TICKETS"} />
        <TicketFilterMenu />
      </Box>

      {status === REQUEST_STATUS.LOADING || status === REQUEST_STATUS.IDLE ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {hasNoTickets(tickets) && <NoTicketsAnnouncement />}

          <Paper sx={{ overflow: "hidden" }}>
            <FolderList subheader={rest.subheader} data={tickets}>
              {(ticket, index) => (
                <FolderItemContainer
                  {...rest} // pass through other props
                  key={`${ticket.id}-${index}`}
                  document={formatDocumentWithIntl(ticket)}
                  onItemClick={handleItemClick}
                  readTicketsIds={readTicketsIds}
                  onClick={handleOpenTicket}
                />
              )}
            </FolderList>
          </Paper>
        </>
      )}
    </DocumentLayout>
  );
}

/**
 * Following fields need translation
 * processingState = "New"
 * serviceType = {
 *  id: 32
 *  name: "Graffiti"
 * }
 */
const formatDocument = (formatMessage) => (document) => {
  const newDocument = { ...document };

  if (newDocument.serviceType && newDocument.serviceType.name) {
    // avoid mutating nested (possibly frozen) objects
    newDocument.serviceType = {
      ...newDocument.serviceType,
      name: formatMessage({
        id: getServiceTypeLanguageIdByName(newDocument.serviceType.name),
      }),
    };
  }

  if (newDocument.processingState) {
    const state = getStateByID(newDocument.processingState);

    if (state && state.i18n) {
      newDocument.processingStatei18n = formatMessage({ id: state.i18n });
    }
  }

  return newDocument;
};

const NoTicketsAnnouncement = () => (
  <AnnouncementIcon
    color="disabled"
    sx={{ display: "flex", margin: "50% auto", fontSize: "10rem" }}
  />
);

export default TicketListContainer;
